// index.ts — der Verteiler fuer alle Bausteine.
//
// Ein Baustein steht in Webflow als Wrapper mit zwei Attributen:
//
//   <div data-module="baustein" data-baustein="vergleichstabelle" ...>
//     <pre data-quelle>Merkmal | Teilverkauf | Niessbrauch ...</pre>
//   </div>
//
// data-module="baustein" holt dieses Modul ueber die Registry in app.ts,
// data-baustein sagt, welcher der sieben Bausteine gebaut wird. Der Inhalt
// steht als Rohtext in [data-quelle] (per CSS ausgeblendet, damit vor dem
// Rendern nichts aufblitzt), alle Einstellungen sind weitere data-Attribute
// am Wrapper — die Uebersicht steht in docs/landingpage-baukasten.md.
import { leerHinweis, lesQuelle } from '@lib/bausteine/dom';
import { zerlegeEinstellungen } from '@lib/bausteine/zerlege';
import { vergleichstabelle } from './vergleichstabelle';
import { checkliste } from './checkliste';
import { paragrafKarten } from './paragraf-karten';
import { rechenbeispiel } from './rechenbeispiel';
import { anbieterKarten } from './anbieter-karten';
import { prozessSchiene } from './prozess-schiene';
import { balkenVergleich } from './balken-vergleich';

type Bauen = (wurzel: HTMLElement, quelle: string) => Node;

const BAUSTEINE: Record<string, Bauen> = {
  vergleichstabelle,
  checkliste,
  'paragraf-karten': paragrafKarten,
  rechenbeispiel,
  'anbieter-karten': anbieterKarten,
  'prozess-schiene': prozessSchiene,
  'balken-vergleich': balkenVergleich,
};

export function initBausteine(): void {
  const wurzeln = document.querySelectorAll<HTMLElement>('[data-module="baustein"]');

  wurzeln.forEach((wurzel) => {
    if (wurzel.dataset.scriptInitialized) return;
    wurzel.dataset.scriptInitialized = 'true';

    const art = (wurzel.getAttribute('data-baustein') ?? '').trim().toLowerCase();
    const bauen = BAUSTEINE[art];

    if (!bauen) {
      // Der Hinweis landet auch auf der Seite, nicht nur in der Konsole: ein
      // vertippter Baustein-Name faellt sonst erst auf, wenn ein leerer Fleck
      // in der veroeffentlichten Seite gefunden wird.
      console.warn(
        `[Baustein] "${art}" unbekannt. Verfügbar: ${Object.keys(BAUSTEINE).join(', ')}`,
      );
      wurzel.appendChild(
        leerHinweis(`Baustein "${art}" unbekannt. Verfügbar: ${Object.keys(BAUSTEINE).join(', ')}`),
      );
      return;
    }

    // Erst lesen, dann bauen, dann die Quelle entfernen — die Reihenfolge
    // zaehlt, weil lesQuelle die Quelle im Wrapper sucht.
    //
    // @-Zeilen im Rohtext (z. B. "@titel: Rechenbeispiel") werden zu
    // data-Attributen am Wrapper. Noetig, weil die Wrapper Webflow-Komponenten
    // sind, deren Attribute sich nicht je Instanz aendern lassen — der Rohtext
    // schon. data-module und data-baustein sind davon ausgenommen, sonst
    // koennte eine Textzeile den Baustein-Typ umbiegen.
    const { einstellungen, inhalt } = zerlegeEinstellungen(lesQuelle(wurzel));
    for (const [name, wert] of Object.entries(einstellungen)) {
      if (name === 'module' || name === 'baustein') continue;
      wurzel.setAttribute(`data-${name}`, wert);
    }

    const markup = bauen(wurzel, inhalt);

    wurzel.querySelector('[data-quelle]')?.remove();
    wurzel.appendChild(markup);
  });
}
